import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../utils/api';
import apiClient from '../utils/api';
import { MasterDataBootstrapResponse, OnboardingChecklistResponse } from '../types/api';

interface UseApiOptions {
  dependencies?: any[];
  skip?: boolean;
}

export const useApi = <T>(
  endpoint: string,
  options: UseApiOptions = {}
) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuth();

  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  const fetchData = async () => {
    if (options.skip || !token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(normalizedEndpoint);
      setData(response.data);
    } catch (err: any) {
      // Don't treat 404 as an error for selected-location endpoint
      if (normalizedEndpoint.includes('/auth/me/selected-location') && err.response?.status === 404) {
        setData(null);
      } else {
        setError(err.response?.data?.detail || err.message || 'An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, options.dependencies || [normalizedEndpoint, token]);

  const refetch = () => fetchData();

  return { data, loading, error, refetch };
};

export const useMutation = <T, P = any>() => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = async (
    endpoint: string,
    data?: P,
    method: 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'POST'
  ): Promise<T | null> => {
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    try {
      setLoading(true);
      setError(null);

      let response;
      switch (method) {
        case 'POST':
          response = await apiClient.post(normalizedEndpoint, data);
          break;
        case 'PUT':
          response = await apiClient.put(normalizedEndpoint, data);
          break;
        case 'PATCH':
          response = await apiClient.patch(normalizedEndpoint, data);
          break;
        case 'DELETE':
          response = await apiClient.delete(normalizedEndpoint);
          break;
        default:
          throw new Error('Unsupported method');
      }
      return response.data;
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, 'An error occurred');
      setError(errorMessage);
      // Many callers use `err.message` directly (and don't call getErrorMessage),
      // so rethrow a normalized Error to ensure backend `detail` shows in UI.
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return { mutate, loading, error };
};

// Hook for master data (scoped to current location)
export const useMasterData = (category: string, locationId?: string | null) => {
  const loc = locationId ?? null;
  const query = loc ? `?location_id=${encodeURIComponent(loc)}` : '';
  return useApi<any[]>(
    `/master-data/${category}${query}`,
    { skip: !loc, dependencies: [category, loc] }
  );
};

// Hook for locations
export const useLocations = () => {
  return useApi<any[]>('/locations');
};

// Hook for ponds by location
export const usePonds = (locationId: string | null) => {
  return useApi<any[]>(
    `/locations/${locationId}/ponds`,
    { skip: !locationId, dependencies: [locationId] }
  );
};

// Hook for dashboard stats
export const useDashboard = (locationId: string | null) => {
  return useApi<any>(
    `/dashboard/${locationId}`,
    { skip: !locationId, dependencies: [locationId] }
  );
};

// Hook for user's selected location
export const useSelectedLocation = () => {
  return useApi<any>('/auth/me/selected-location');
};

// Hook for updating user's selected location
export const useUpdateLocation = () => {
  const { mutate, loading, error } = useMutation<any, { selected_location_id: string | null }>();
  
  const updateLocation = async (locationId: string | null) => {
    return await mutate('/auth/me/location', { selected_location_id: locationId }, 'PATCH');
  };
  
  return { updateLocation, loading, error };
};

export const getOnboardingChecklist = async (
  locationId: string,
  businessType?: 'fish' | 'goat'
): Promise<OnboardingChecklistResponse> => {
  const response = await apiClient.get<OnboardingChecklistResponse>('/onboarding/checklist', {
    params: {
      location_id: locationId,
      ...(businessType ? { business_type: businessType } : {}),
    },
  });
  return response.data;
};

export const bootstrapMasterData = async (
  locationId: string,
  businessTypes?: string[]
): Promise<MasterDataBootstrapResponse> => {
  const response = await apiClient.post<MasterDataBootstrapResponse>('/master-data/bootstrap', {
    location_id: locationId,
    ...(businessTypes && businessTypes.length > 0 ? { business_types: businessTypes } : {}),
  });
  return response.data;
};